import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { ChatThread } from "types/ChatThread";
import type { Message } from "types/Message";
import { SCOPE_FILES_QUERY_KEY } from "services/fileApi/hooks/scopeFilesKeys";
import { putThreadMessages } from "../methods/putThreadMessages";
import { MESSAGES_WRITE_OPTIONS } from "./messagesWriteOptions";

interface PutMessagesVariables {
  threadId: string;
  messages: Message[];
}

/**
 * Stable key so consumers can observe an in-flight messages PUT via
 * `useIsMutating`. The focus-refetch in `Chat.tsx` gates on this so a GET can't
 * resolve with pre-directive server state and clobber a just-committed directive
 * (ADR 056).
 */
export const PUT_MESSAGES_MUTATION_KEY = ["cc-db-api", "putMessages"];

/**
 * Replaces a chat thread's full message list and updates the relevant caches
 * in place.
 */
export default function usePutMessagesMutation() {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PUT_MESSAGES_MUTATION_KEY,
    // Each PUT replaces the whole list, so they must not finish out of order.
    // One scope, shared by all threads, sends them one at a time (ADR 071).
    scope: { id: "cc-db-api-putMessages" },
    mutationFn: ({ threadId, messages }: PutMessagesVariables) =>
      putThreadMessages(threadId, messages, getAccessToken),
    ...MESSAGES_WRITE_OPTIONS,
    onSuccess: ({ messages, timestamp }, { threadId }) => {
      // Update the messages cache directly. Avoiding invalidate so a write
      // doesn't trigger an extra GET /threads/:id/messages while the user
      // is mid-flow.
      queryClient.setQueryData<{ messages: Message[] }>(
        ["cc-db-api", "threads", threadId, "messages"],
        { messages }
      );
      // Bump the thread's timestamp in the cached list so the sidebar
      // re-sorts without a refetch.
      queryClient.setQueryData<ChatThread[]>(["cc-db-api", "threads"], (prev) =>
        prev?.map((t) => (t.id === threadId ? { ...t, timestamp } : t))
      );
      // Uploads, deletes, and assistant turns can all change the files.
      queryClient.invalidateQueries({
        queryKey: SCOPE_FILES_QUERY_KEY(threadId),
      });
    },
  });
}
